<template>
  <div class="history-panel" @scroll="onScroll" ref="scrollEl">
    <div v-if="!shopCustomerId" class="caption text-center pa-6" style="color: grey">
      {{ use_language.shopcustomerid_not_found }}
    </div>

    <template v-else>
      <div v-if="loading && orders.length === 0" class="text-center pa-6">
        <v-progress-circular indeterminate color="primary" size="24" />
      </div>

      <div v-else-if="orders.length === 0" class="caption text-center pa-6" style="color: grey">
        <v-icon size="40" color="grey lighten-1">mdi-package-variant-closed</v-icon>
        <div class="mt-2">{{ use_language.customer_no_orders }}</div>
      </div>

      <div v-else class="pa-2">
        <v-card
          v-for="o in orders"
          :key="o.id"
          outlined
          class="mb-2 order-card"
          @click="openOrder(o.id)"
        >
          <v-card-text class="pa-3">
            <div class="d-flex align-center mb-1">
              <a class="font-weight-medium" @click.stop="openOrder(o.id)">
                {{ formatOrderId(o.id) }}
              </a>
              <v-avatar v-if="o.channelLogo" size="18" class="ml-2">
                <v-img :src="o.channelLogo" />
              </v-avatar>
              <v-spacer />
              <span class="font-weight-bold">฿{{ formatPrice(o.orderTotalAmount) }}</span>
            </div>
            <div class="d-flex align-center">
              <small style="color: grey">{{ formatDt(o.createdDatetime) }}</small>
              <v-spacer />
              <small :style="{ color: statusColor(o.shippingStatusID) }">
                {{ o.shippingStatusName }}
              </small>
            </div>
            <div class="d-flex align-center mt-2">
              <img v-if="o.expressCompanyLogo" :src="o.expressCompanyLogo" style="height: 18px" />
              <v-chip v-if="o.isCOD" x-small color="red" outlined class="ml-2">COD</v-chip>
              <v-spacer />
              <small v-if="o.trackingNo" style="color: grey">{{ o.trackingNo }}</small>
            </div>
          </v-card-text>
        </v-card>
        <div v-if="loadingMore" class="py-2 text-center">
          <v-progress-circular indeterminate size="18" color="primary" />
        </div>
        <div v-else-if="eof && orders.length > 0" class="caption text-center py-2" style="color: grey">
          — {{ use_language.all_complete }} —
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import axios from "axios";
import { orderService_dotnet } from "@/website/global";
import { get_languages } from "@/website/global_function";

const PAGE_SIZE = 12;

const STATUS_COLOR = {
  10: "black", 20: "#E40078", 30: "#ff7ac0",
  40: "orange", 50: "blue", 60: "#9acd32",
  70: "green", 90: "red",
};

export default {
  name: "HistoryPanel",
  props: {
    shopId: { type: [String, Number], required: true },
    shopCustomerId: { type: [String, Number], default: null },
  },
  data: () => ({
    use_language: {},
    orders: [],
    loading: false,
    loadingMore: false,
    eof: false,
  }),
  watch: {
    shopCustomerId() {
      this.orders = [];
      this.eof = false;
      if (this.shopCustomerId) this.load(true);
    },
  },
  async created() {
    this.use_language = await this.get_languages(localStorage.getItem("set_language"));
    if (this.shopCustomerId) this.load(true);
  },
  methods: {
    get_languages,
    formatPrice(v) {
      if (v == null) return "0.00";
      return Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatOrderId(id) {
      return "PA" + String(id).padStart(6, "0");
    },
    formatDt(s) {
      if (!s) return "";
      const d = new Date(s);
      if (isNaN(d.getTime())) return s;
      return d.toLocaleString("th-TH", {
        day: "2-digit", month: "2-digit", year: "2-digit",
        hour: "2-digit", minute: "2-digit",
      });
    },
    statusColor(id) { return STATUS_COLOR[id] || "grey"; },

    async load(reset = false) {
      if (!this.shopCustomerId) return;
      if (reset) this.loading = true;
      else this.loadingMore = true;
      try {
        const headers = {
          "Content-Type": "application/json",
          Authorization: "Bearer " + localStorage.getItem("authorization_token"),
        };
        const res = await axios.post(
          orderService_dotnet + "Order/get-order-list",
          {
            shopID: this.shopId,
            shopCustomerID: this.shopCustomerId,
            skip: reset ? 0 : this.orders.length,
            take: PAGE_SIZE,
          },
          { headers }
        );
        const list = (res.data && res.data.orderList) || [];
        if (reset) this.orders = list;
        else this.orders = this.orders.concat(list);
        if (list.length < PAGE_SIZE) this.eof = true;
      } catch (err) {
        this.$swal && this.$swal({
          icon: "error",
          title: this.use_language.load_history_failed,
          text: err.message || this.use_language.error,
        });
      } finally {
        this.loading = false;
        this.loadingMore = false;
      }
    },
    onScroll(e) {
      if (this.eof || this.loadingMore) return;
      const el = e.target;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 40) {
        this.load(false);
      }
    },
    openOrder(id) {
      window.open("order-detail?id=" + id, "_blank");
    },
  },
};
</script>

<style scoped>
.history-panel {
  height: 100%;
  overflow-y: auto;
}
.order-card { cursor: pointer; }
.order-card:hover { background: #f5f5f5; }
</style>
